import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle,
    CalendarIcon,
    CheckCircle,
    Clock,
    Loader2,
    Send,
    X,
} from "lucide-react";
import { cn } from "../../utils/classMerger";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../components/misc/Popover";
import { Button } from "../../components/misc/Button";
import { format } from "date-fns";
import CustomDatePicker from "../../components/misc/CutomDatePicker";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/misc/Select";
import RichTextEditor from "../../components/misc/rich-text-editor/RichTextEditor";

interface AddNotificationProps {
    feedback: {
        type: "success" | "error";
        message: string;
    } | null;
    setFeedback: React.Dispatch<
        React.SetStateAction<{
            type: "success" | "error";
            message: string;
        } | null>
    >;
    title: string;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    message: string;
    setMessage: React.Dispatch<React.SetStateAction<string>>;
    type: "announcement";
    setType: React.Dispatch<React.SetStateAction<"announcement">>;
    isMobile: boolean;
    isCalendarOpen: boolean;
    setIsCalendarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    expiryDate: Date | null;
    setExpiryDate: React.Dispatch<React.SetStateAction<Date | null>>;
    expiryHour: number;
    setExpiryHour: React.Dispatch<React.SetStateAction<number>>;
    expiryMinute: number;
    setExpiryMinute: React.Dispatch<React.SetStateAction<number>>;
    expiryPeriod: "AM" | "PM";
    setExpiryPeriod: React.Dispatch<React.SetStateAction<"AM" | "PM">>;
    isSending: boolean;
    setIsSending: React.Dispatch<React.SetStateAction<boolean>>;
    handleSubmit: () => void;
    handleExpiryDateChange: (date: Date) => void;
    handleHourChange: (hour: string) => void;
    handleMinuteChange: (minute: string) => void;
    handlePeriodChange: (period: string) => void;
    insertMarkdownFormat: (format: string) => void;
    activeMessageTab: "write" | "preview";
    setActiveMessageTab: React.Dispatch<
        React.SetStateAction<"write" | "preview">
    >;
}

const AddNotification: React.FC<AddNotificationProps> = ({
    feedback,
    setFeedback,
    title,
    setTitle,
    message,
    setMessage,
    type,
    setType,
    isMobile,
    isCalendarOpen,
    setIsCalendarOpen,
    expiryDate,
    expiryHour,
    expiryMinute,
    expiryPeriod,
    isSending,
    handleSubmit,
    handleExpiryDateChange,
    handleMinuteChange,
    handlePeriodChange,
    insertMarkdownFormat,
    activeMessageTab,
    setActiveMessageTab,
    handleHourChange,
}) => {
    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    return (
        <motion.div
            key="compose"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="overflow-visible" // Fix for issue #1: Prevent unwanted scrollbar
        >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Compose Form */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                                <Send className="w-5 h-5 mr-2 text-secondary" />
                                Compose Announcement
                            </h2>
                            <p className="text-gray-500 text-sm mt-1">
                                Create a new announcement to send to all users
                            </p>
                        </div>

                        <div className="p-6 space-y-6">
                            <AnimatePresence>
                                {feedback && (
                                    <motion.div
                                        initial={{
                                            opacity: 0,
                                            height: 0,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            height: "auto",
                                        }}
                                        exit={{
                                            opacity: 0,
                                            height: 0,
                                        }}
                                        className={`p-4 rounded-lg flex items-start ${
                                            feedback.type === "success"
                                                ? "bg-green-50 border border-green-200"
                                                : "bg-red-50 border border-red-200"
                                        }`}
                                    >
                                        <div className="flex-shrink-0 mt-0.5">
                                            {feedback.type === "success" ? (
                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                            ) : (
                                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                            )}
                                        </div>
                                        <div className="ml-3">
                                            <h3
                                                className={`text-sm font-medium ${
                                                    feedback.type === "success"
                                                        ? "text-green-800"
                                                        : "text-red-800"
                                                }`}
                                            >
                                                {feedback.type === "success"
                                                    ? "Success"
                                                    : "Error"}
                                            </h3>
                                            <div
                                                className={`mt-1 text-sm ${
                                                    feedback.type === "success"
                                                        ? "text-green-700"
                                                        : "text-red-700"
                                                }`}
                                            >
                                                {feedback.message}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="ml-auto flex-shrink-0 -mt-1 -mr-1 p-1 rounded-full hover:bg-gray-200"
                                            onClick={() => setFeedback(null)}
                                        >
                                            <X className="w-4 h-4 text-gray-400" />
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Type
                                    </label>
                                    <div className="grid grid-cols-4 gap-3">
                                        {[
                                            {
                                                value: "announcement",
                                                label: "Announcement",
                                                icon: (
                                                    <AlertTriangle className="w-4 h-4" />
                                                ),
                                            },
                                        ].map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() =>
                                                    setType(option.value as any)
                                                }
                                                className={`flex items-center justify-center p-3 rounded-lg border-2 transition-all ${
                                                    type === option.value
                                                        ? "border-lightSecondary bg-[#fdf6e7] text-secondary"
                                                        : "border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50"
                                                }`}
                                            >
                                                <div className="flex flex-col items-center space-y-1">
                                                    <div
                                                        className={`p-2 rounded-full ${
                                                            type ===
                                                            option.value
                                                                ? "bg-secondary text-white"
                                                                : "bg-gray-200"
                                                        }`}
                                                    >
                                                        {option.icon}
                                                    </div>
                                                    <span className="text-xs font-medium">
                                                        {option.label}
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label
                                        htmlFor="title"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        Title
                                    </label>
                                    <input
                                        id="title"
                                        value={title}
                                        onChange={(e) =>
                                            setTitle(e.target.value)
                                        }
                                        maxLength={100}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary transition-colors outline-none"
                                        placeholder="Enter announcement title"
                                    />
                                    <div className="mt-1 text-xs text-gray-500 flex justify-between">
                                        <span>
                                            Make it clear and attention-grabbing
                                        </span>
                                        <span>{title.length}/100</span>
                                    </div>
                                </div>
                                <div>
                                    <label
                                        htmlFor="message"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        Message
                                    </label>
                                    <div className="w-full">
                                        <RichTextEditor
                                            message={message}
                                            onMessageChange={(
                                                content: string
                                            ) => {
                                                setMessage(content);
                                            }}
                                            limit={2000}
                                        />
                                    </div>
                                </div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Expiry Date & Time
                                </label>
                                <div
                                    className={cn(
                                        "flex items-center space-x-2 text-primary",
                                        isMobile
                                            ? "flex-col space-y-2"
                                            : "flex-row"
                                    )}
                                >
                                    {/* Date picker */}
                                    <Popover
                                        open={isCalendarOpen}
                                        onOpenChange={setIsCalendarOpen}
                                    >
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className={cn(
                                                    "w-[240px] justify-start text-left font-normal",
                                                    !expiryDate &&
                                                        "text-muted-foreground"
                                                )}
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {expiryDate ? (
                                                    format(expiryDate, "PPP")
                                                ) : (
                                                    <span>Pick a date</span>
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            className="w-auto p-0"
                                            align="start"
                                        >
                                            <CustomDatePicker
                                                date={expiryDate || new Date()}
                                                setDate={handleExpiryDateChange}
                                                restriction="month"
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    {/* Time picker */}
                                    <div className="flex items-center space-x-2">
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        <Select
                                            value={expiryHour.toString()}
                                            onValueChange={handleHourChange}
                                        >
                                            <SelectTrigger className="w-[70px]">
                                                <SelectValue placeholder="HH" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {hourOptions.map((h) => (
                                                    <SelectItem
                                                        key={h}
                                                        value={h.toString()}
                                                    >
                                                        {h
                                                            .toString()
                                                            .padStart(2, "0")}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={expiryMinute.toString()}
                                            onValueChange={handleMinuteChange}
                                        >
                                            <SelectTrigger className="w-[70px]">
                                                <SelectValue placeholder="MM" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {minuteOptions.map((m) => (
                                                    <SelectItem
                                                        key={m}
                                                        value={m.toString()}
                                                    >
                                                        {m
                                                            .toString()
                                                            .padStart(2, "0")}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={expiryPeriod}
                                            onValueChange={handlePeriodChange}
                                        >
                                            <SelectTrigger className="w-[70px]">
                                                <SelectValue placeholder="AM/PM" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="AM">
                                                    AM
                                                </SelectItem>
                                                <SelectItem value="PM">
                                                    PM
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                    The announcement will expire at this date
                                    and time
                                </div>
                            </div>

                            <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                                <Button
                                    type="submit"
                                    onClick={handleSubmit}
                                    disabled={
                                        isSending ||
                                        !title.trim() ||
                                        !message.trim() ||
                                        !expiryDate
                                    }
                                >
                                    {isSending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4 mr-2" />
                                            Send to All Users
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default AddNotification;
